import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Role } from '../../../model/shared/user.model';

export interface RoleState {
    selectedRoleCode: string | null;
    selectedRoleLabel: string | null;
    selectedRoleName: string | null;
    selectedBusinessUnitCode: string | null;
    selectedRegionCode: string | null;
    availableRoles: Role[];
    isHydrated: boolean;
}

@Injectable({
    providedIn: 'root'
})

export class RoleStateService {
    private readonly stateSubject = new BehaviorSubject<RoleState>({
        selectedRoleCode: null,
        selectedRoleLabel: null,
        selectedRoleName: null,
        selectedBusinessUnitCode: null,
        selectedRegionCode: null,
        availableRoles: [],
        isHydrated: false
    });

    readonly roleState$: Observable<RoleState> = this.stateSubject.asObservable();
    readonly selectedRole$: Observable<Role | null> = this.roleState$.pipe(
        map((state) => state.availableRoles.find((role) => role.role_code === state.selectedRoleCode) || null)
    );

    getSnapshot(): RoleState {
        return this.stateSubject.value;
    }

    // Keep business-unit and region context in the same state object as the selected role.
    setContext(businessUnitCode: string | null, regionCode: string | null): void {
        this.stateSubject.next({
            ...this.stateSubject.value,
            selectedBusinessUnitCode: businessUnitCode,
            selectedRegionCode: regionCode
        });
    }

    // Refresh the list of switchable roles and recover gracefully if the current role is no longer valid.
    setAvailableRoles(roles: Role[]): void {
        const state = this.stateSubject.value;
        const normalizedRoles = Array.isArray(roles) ? roles : [];
        const roleStillValid = normalizedRoles.some(role => role.role_code === state.selectedRoleCode);

        let selectedRoleCode = state.selectedRoleCode;
        let selectedRoleLabel = state.selectedRoleLabel;
        let selectedRoleName = state.selectedRoleName;

        if (!roleStillValid) {
            const fallbackRole = normalizedRoles[0] || null;
            selectedRoleCode = fallbackRole?.role_code || null;
            selectedRoleLabel = fallbackRole?.role_label || null;
            selectedRoleName = fallbackRole?.role_name || null;
        }

        this.stateSubject.next({
            ...state,
            availableRoles: normalizedRoles,
            selectedRoleCode,
            selectedRoleLabel,
            selectedRoleName
        });
    }

    // Use this when the UI explicitly picks a role and that choice should become the new source of truth.
    setSelectedRole(role: Role, businessUnitCode?: string, regionCode?: string): void {
        this.stateSubject.next({
            ...this.stateSubject.value,
            selectedRoleCode: role?.role_code || null,
            selectedRoleLabel: role?.role_label || null,
            selectedRoleName: role?.role_name || null,
            selectedBusinessUnitCode: businessUnitCode ?? this.stateSubject.value.selectedBusinessUnitCode,
            selectedRegionCode: regionCode ?? this.stateSubject.value.selectedRegionCode
        });
    }
}
